import { useMaskEditorDataStore } from '@/stores/maskEditorDataStore'
import { useMaskEditorStore } from '@/stores/maskEditorStore'
import { useNodeOutputStore } from '@/stores/nodeOutputStore'
import type {
  EditorOutputData,
  EditorOutputLayer,
  ImageRef
} from '@/stores/maskEditorDataStore'
import { isCloud } from '@/platform/distribution/types'
import { api } from '@/scripts/api'
import { app } from '@/scripts/app'
import { createAnnotatedPath } from '@/utils/createAnnotatedPath'
import type { LGraphNode } from '@/lib/litegraph/src/litegraph'
//!nordy - Nordy 백엔드 마스크 업로드 시 에셋 타입 식별을 위한 AssetKind 임포트
import { AssetKind } from '@/nordy/interfaces'

//!nordy - URL이 직접 HTTP URL(S3/CloudFront 등)인지 확인
function isDirectUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://')
}

//!nordy - 서버 업로드용 original_ref 생성.
// ComfyUI 서버의 upload_mask 핸들러는 original_ref.filename이
// 원본 이미지를 다운로드할 수 있는 URL(S3 URL)이어야 알파 합성이 가능함.
// S3 URL이 있으면 해당 URL을 사용.
function toFetchableRef(ref: ImageRef): ImageRef {
  if (ref.url && isDirectUrl(ref.url)) {
    return { ...ref, filename: ref.url }
  }
  return ref
}

// Private layer filename functions
interface ImageLayerFilenames {
  maskedImage: string
  paint: string
  paintedMaskedImage: string
}

function imageLayerFilenamesByTimestamp(
  timestamp: number
): ImageLayerFilenames {
  return {
    maskedImage: `clipspace-mask-${timestamp}.png`,
    paint: `clipspace-paint-${timestamp}.png`,
    paintedMaskedImage: `clipspace-painted-masked-${timestamp}.png`
  }
}

export function useMaskEditorSaver() {
  const dataStore = useMaskEditorDataStore()
  const editorStore = useMaskEditorStore()
  const nodeOutputStore = useNodeOutputStore()

  const save = async (): Promise<void> => {
    const sourceNode = dataStore.sourceNode as LGraphNode
    if (!sourceNode || !dataStore.inputData) {
      throw new Error('No source node or input data')
    }

    try {
      const outputData = await prepareOutputData()
      dataStore.outputData = outputData

      await updateNodePreview(sourceNode, outputData)

      await uploadAllLayers(outputData)

      updateNodeWithServerReferences(sourceNode, outputData)

      app.canvas?.setDirty(true)
    } catch (error) {
      console.error('[MaskEditorSaver] Save failed:', error)
      throw error
    }
  }

  async function prepareOutputData(): Promise<EditorOutputData> {
    const maskCanvas = editorStore.maskCanvas
    const paintCanvas = editorStore.rgbCanvas
    const imgCanvas = editorStore.imgCanvas

    if (!maskCanvas || !paintCanvas || !imgCanvas) {
      throw new Error('Canvas not initialized')
    }

    const timestamp = Date.now()
    const filenames = imageLayerFilenamesByTimestamp(timestamp)

    // const [maskedImage, paintLayer, paintedMaskedImage] = await Promise.all([
    //   createMaskedImage(imgCanvas, maskCanvas, filenames.maskedImage),
    //   createPaintLayer(paintCanvas, filenames.paint),
    //   createPaintedMaskedImage(
    //     imgCanvas,
    //     paintCanvas,
    //     maskCanvas,
    //     filenames.paintedMaskedImage
    //   )
    // ])
    const paintedMaskedImage = await createPaintedMaskedImage(
      imgCanvas,
      paintCanvas,
      maskCanvas,
      filenames.paintedMaskedImage
    )

    return {
      // maskedImage,
      // paintLayer,
      paintedMaskedImage
    }
  }

  //!nordy - createMaskedImage 현재 미사용, 향후 마스크 레이어 지원을 위해 보존
  // async function createMaskedImage(
  //   imgCanvas: HTMLCanvasElement,
  //   maskCanvas: HTMLCanvasElement,
  //   filename: string
  // ): Promise<EditorOutputLayer> {
  //   const canvas = document.createElement('canvas')
  //   canvas.width = imgCanvas.width
  //   canvas.height = imgCanvas.height
  //   const ctx = canvas.getContext('2d')!
  //
  //   ctx.drawImage(imgCanvas, 0, 0)
  //
  //   const maskCtx = maskCanvas.getContext('2d')!
  //   const maskData = maskCtx.getImageData(
  //     0,
  //     0,
  //     maskCanvas.width,
  //     maskCanvas.height
  //   )
  //
  //   const refinedMaskData = new Uint8ClampedArray(maskData.data.length)
  //   for (let i = 0; i < maskData.data.length; i += 4) {
  //     refinedMaskData[i] = 0
  //     refinedMaskData[i + 1] = 0
  //     refinedMaskData[i + 2] = 0
  //     refinedMaskData[i + 3] = 255 - maskData.data[i + 3]
  //   }
  //
  //   const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  //   for (let i = 0; i < imageData.data.length; i += 4) {
  //     imageData.data[i + 3] = refinedMaskData[i + 3]
  //   }
  //   ctx.putImageData(imageData, 0, 0)
  //
  //   const blob = await canvasToBlob(canvas)
  //   const ref = createFileRef(filename)
  //
  //   return { canvas, blob, ref }
  // }

  //!nordy - createPaintLayer 현재 미사용, 향후 페인트 레이어 지원을 위해 보존
  // async function createPaintLayer(
  //   paintCanvas: HTMLCanvasElement,
  //   filename: string
  // ): Promise<EditorOutputLayer> {
  //   const canvas = cloneCanvas(paintCanvas)
  //   const blob = await canvasToBlob(canvas)
  //   const ref = createFileRef(filename)
  //
  //   return { canvas, blob, ref }
  // }

  async function createPaintedMaskedImage(
    imgCanvas: HTMLCanvasElement,
    paintCanvas: HTMLCanvasElement,
    maskCanvas: HTMLCanvasElement,
    filename: string
  ): Promise<EditorOutputLayer> {
    const canvas = document.createElement('canvas')
    canvas.width = imgCanvas.width
    canvas.height = imgCanvas.height
    const ctx = canvas.getContext('2d')!

    ctx.drawImage(imgCanvas, 0, 0)

    ctx.globalCompositeOperation = 'source-over'
    ctx.drawImage(paintCanvas, 0, 0)

    const maskCtx = maskCanvas.getContext('2d')!
    const maskData = maskCtx.getImageData(
      0,
      0,
      maskCanvas.width,
      maskCanvas.height
    )

    const refinedMaskData = new Uint8ClampedArray(maskData.data.length)
    for (let i = 0; i < maskData.data.length; i += 4) {
      refinedMaskData[i] = 0
      refinedMaskData[i + 1] = 0
      refinedMaskData[i + 2] = 0
      refinedMaskData[i + 3] = 255 - maskData.data[i + 3]
    }

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    for (let i = 0; i < imageData.data.length; i += 4) {
      imageData.data[i + 3] = refinedMaskData[i + 3]
    }
    ctx.putImageData(imageData, 0, 0)

    const blob = await canvasToBlob(canvas)
    const ref = createFileRef(filename)

    return { canvas, blob, ref }
  }

  async function uploadAllLayers(outputData: EditorOutputData): Promise<void> {
    const sourceRef = dataStore.inputData!.sourceRef

    // const [actualMaskedRef, actualPaintRef, actualPaintedMaskedRef] =
    //   await Promise.all([
    //     uploadMask(outputData.maskedImage, sourceRef),
    //     uploadImage(outputData.paintLayer, sourceRef),
    //     uploadImage(outputData.paintedMaskedImage, sourceRef)
    //   ])
    //
    // outputData.maskedImage.ref = actualMaskedRef
    // outputData.paintLayer.ref = actualPaintRef
    const actualPaintedMaskedRef = await uploadImage(
      outputData.paintedMaskedImage,
      sourceRef
    )
    outputData.paintedMaskedImage.ref = actualPaintedMaskedRef
  }

  //!nordy - uploadMask 현재 미사용, 향후 마스크 업로드 지원을 위해 보존
  // async function uploadMask(
  //   layer: EditorOutputLayer,
  //   originalRef: ImageRef
  // ): Promise<ImageRef> {
  //   const formData = new FormData()
  //   formData.append('image', layer.blob, layer.ref.filename)
  //   //!nordy - 서버가 requests.get(original_ref.filename)으로 원본 이미지를 가져오므로
  //   // filename은 단순 파일명이 아닌 접근 가능한 S3 URL이어야 함
  //   formData.append('original_ref', JSON.stringify(toFetchableRef(originalRef)))
  //   formData.append('type', 'input')
  //   formData.append('subfolder', 'clipspace')
  //   //!nordy - Nordy 백엔드가 업로드된 에셋을 분류할 수 있도록 에셋 종류 첨부
  //   formData.append('kind', AssetKind.Image)
  //
  //   const response = await api.fetchApi('/upload/mask', {
  //     method: 'POST',
  //     body: formData
  //   })
  //
  //   if (!response.ok) {
  //     throw new Error(`Failed to upload mask: ${layer.ref.filename}`)
  //   }
  //
  //   try {
  //     const data = await response.json()
  //     if (data?.name) {
  //       return {
  //         filename: data.name,
  //         subfolder: data.subfolder || layer.ref.subfolder,
  //         type: data.type || layer.ref.type,
  //         //!nordy - Nordy 백엔드 응답에서 S3 URL 캡처
  //         url: typeof data.url === 'string' ? data.url : undefined
  //       }
  //     }
  //   } catch (error) {
  //     console.warn('[MaskEditorSaver] Failed to parse upload response:', error)
  //   }
  //
  //   return layer.ref
  // }

  async function uploadImage(
    layer: EditorOutputLayer,
    originalRef: ImageRef
  ): Promise<ImageRef> {
    const formData = new FormData()
    formData.append('image', layer.blob, layer.ref.filename)
    //!nordy - 서버가 requests.get(original_ref.filename)으로 원본 이미지를 가져오므로
    // filename은 단순 파일명이 아닌 접근 가능한 S3 URL이어야 함
    formData.append('original_ref', JSON.stringify(toFetchableRef(originalRef)))
    formData.append('type', 'input')
    formData.append('subfolder', 'clipspace')
    //!nordy - Nordy 백엔드가 업로드된 에셋을 분류할 수 있도록 에셋 종류 첨부
    formData.append('kind', AssetKind.Image)

    //!nordy - image가 아니라 mask 엔드포인트로 변경 (그래야 inputMaskAsset 정상 생성됨)
    // const response = await api.fetchApi('/upload/image', {
    const response = await api.fetchApi('/upload/mask', {
      method: 'POST',
      body: formData
    })

    if (!response.ok) {
      throw new Error(`Failed to upload image: ${layer.ref.filename}`)
    }

    try {
      const data = await response.json()
      if (data?.name) {
        return {
          filename: data.name,
          subfolder: data.subfolder || layer.ref.subfolder,
          type: data.type || layer.ref.type,
          //!nordy - Nordy 백엔드 응답에서 S3 URL 캡처
          url: typeof data.url === 'string' ? data.url : undefined
        }
      }
    } catch (error) {
      console.warn('[MaskEditorSaver] Failed to parse upload response:', error)
    }

    return layer.ref
  }

  async function updateNodePreview(
    node: LGraphNode,
    outputData: EditorOutputData
  ): Promise<void> {
    const canvas = outputData.paintedMaskedImage.canvas
    const dataUrl = canvas.toDataURL('image/png')

    const mainImg = await loadImageFromUrl(dataUrl)
    node.imgs = [mainImg]

    app.canvas?.setDirty(true)
  }

  function updateNodeWithServerReferences(
    node: LGraphNode,
    outputData: EditorOutputData
  ): void {
    const mainRef = outputData.paintedMaskedImage.ref

    node.images = [mainRef]

    const imageWidget = node.widgets?.find((w) => w.name === 'image')
    if (imageWidget) {
      //!nordy - 위젯 값 형식은 Cloud, Nordy, OSS 환경별로 다름:
      // - Nordy (S3 URL): S3 URL을 위젯 값으로 직접 사용
      // - Cloud: 파일명만 (subfolder는 백엔드에서 처리)
      // - OSS: subfolder/filename (전통적 형식)
      let widgetValue: string
      if (mainRef.url && isDirectUrl(mainRef.url)) {
        //!nordy - S3 URL을 위젯 값으로 직접 사용
        widgetValue = mainRef.url
      } else if (isCloud) {
        widgetValue =
          mainRef.filename + (mainRef.type ? ` [${mainRef.type}]` : '')
      } else {
        widgetValue =
          (mainRef.subfolder ? mainRef.subfolder + '/' : '') +
          mainRef.filename +
          (mainRef.type ? ` [${mainRef.type}]` : '')
      }

      imageWidget.value = widgetValue

      if (node.properties) {
        node.properties['image'] = widgetValue
      }

      if (node.widgets_values && node.widgets) {
        const widgetIndex = node.widgets.indexOf(imageWidget)
        if (widgetIndex >= 0) {
          node.widgets_values[widgetIndex] = widgetValue
        }
      }
    }

    node.imgs = undefined
    //!nordy - S3 URL이 있는 경우 URL을 직접 전달하여 createOutputs에서
    // S3 URL로 인식되도록 함. 일반 annotatedPath는 http로 시작하지 않아
    // S3 URL 정보가 소실됨
    if (mainRef.url && isDirectUrl(mainRef.url)) {
      nodeOutputStore.setNodeOutputs(node, mainRef.url, { folder: 'input' })
    } else {
      const annotatedPath = createAnnotatedPath(mainRef.filename, {
        subfolder: mainRef.subfolder,
        rootFolder: mainRef.type
      })
      nodeOutputStore.setNodeOutputs(node, annotatedPath, { folder: 'input' })
    }
    node.graph?.setDirtyCanvas(true)
  }

  function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => resolve(img)
      img.onerror = (error) => {
        console.error('[MaskEditorSaver] Failed to load image:', url, error)
        reject(new Error(`Failed to load image: ${url}`))
      }

      img.src = url
    })
  }

  //!nordy - cloneCanvas 현재 미사용, createPaintLayer 복원 시 필요하여 보존
  // function cloneCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  //   const canvas = document.createElement('canvas')
  //   canvas.width = source.width
  //   canvas.height = source.height
  //   const ctx = canvas.getContext('2d')!
  //   ctx.drawImage(source, 0, 0)
  //   return canvas
  // }

  function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob)
        else reject(new Error('Failed to create blob from canvas'))
      }, 'image/png')
    })
  }

  function createFileRef(filename: string): ImageRef {
    return {
      filename,
      subfolder: 'clipspace',
      type: 'input'
    }
  }

  return {
    save
  }
}
